import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeft, CheckCircle2, Eye, Send } from 'lucide-react';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import { formatInTimezone, useTimezone } from '@kioskgaming/ui';
import AppLayout from '../components/layout/AppLayout';
import { ContactAdminImageList, ContactAdminImagePicker } from '../components/contactAdmin/ContactAdminImages';
import {
  CONTACT_ADMIN_PATH,
  REPLY_MAX,
  STATUS_META,
  announceContactAdminChange,
  apiError,
} from '../components/contactAdmin/contactAdmin';
import {
  confirmSupportTicketResolved,
  fetchSupportTicket,
  replySupportTicket,
  type SupportTicketDetail,
} from '../services/api';

type Message = { id: string; mine: boolean; name: string; text: string; at: string | null; images: Array<{ id: string; url: string }> };

/**
 * One ticket in Contact admin: the conversation with the admin team. On its own ticket the Shop
 * replies and can confirm it is solved; a ticket the admin team shared is read only.
 */
export default function ContactAdminTicketPage() {
  const { ticketNumber = '' } = useParams();
  const { timezone } = useTimezone();
  const [ticket, setTicket] = useState<SupportTicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [attachmentIds, setAttachmentIds] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  /** A new key clears the image picker after a reply goes out. */
  const [pickerKey, setPickerKey] = useState(0);
  const onImages = useCallback((ids: string[], loadingImages: boolean) => {
    setAttachmentIds(ids);
    setUploading(loadingImages);
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await fetchSupportTicket(ticketNumber);
      if (res.success && res.data) {
        setTicket(res.data);
        announceContactAdminChange();
      }
    } catch (error) {
      if ((error as { response?: { status?: number } })?.response?.status === 404) setMissing(true);
      else toast.error('Could not load this ticket');
    } finally {
      setLoading(false);
    }
  }, [ticketNumber]);

  useEffect(() => {
    setLoading(true);
    setMissing(false);
    void load();
  }, [load]);

  const send = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const res = await replySupportTicket(ticketNumber, reply.trim(), attachmentIds);
      if (res.success && res.data) {
        setTicket(res.data);
        setReply('');
        setAttachmentIds([]);
        setPickerKey((k) => k + 1);
        toast.success('Reply sent');
      }
    } catch (error) {
      toast.error(apiError(error, 'Could not send your reply').message);
    } finally {
      setBusy(false);
    }
  };

  const confirmSolved = async () => {
    setBusy(true);
    try {
      const res = await confirmSupportTicketResolved(ticketNumber);
      if (res.success && res.data) {
        setTicket(res.data);
        toast.success('Ticket closed. Thank you!');
      }
    } catch (error) {
      toast.error(apiError(error, 'Could not close the ticket').message);
    } finally {
      setBusy(false);
    }
  };

  const back = (
    <Link to={CONTACT_ADMIN_PATH} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:underline">
      <ArrowLeft className="h-4 w-4" aria-hidden /> Back to Contact admin
    </Link>
  );

  if (loading) {
    return (
      <AppLayout>
        {back}
        <PageLoadingIndicator />
      </AppLayout>
    );
  }

  if (missing || !ticket) {
    return (
      <AppLayout>
        {back}
        <p className="text-sm text-stone-600">This ticket does not exist, or it is not shared with your Shop.</p>
      </AppLayout>
    );
  }

  const shared = ticket.access === 'shared';
  const messages: Message[] = [
    { id: 'original', mine: !shared, name: shared ? 'Request' : 'Your Shop', text: ticket.message, at: ticket.createdAt, images: ticket.attachments || [] },
    ...ticket.replies.map((r) => ({
      id: r.id,
      mine: r.replyType === 'user' && !shared,
      name: r.replyType === 'admin' ? r.adminName || 'Vault Play team' : shared ? 'Request' : 'Your Shop',
      text: r.message,
      at: r.createdAt,
      images: r.attachments || [],
    })),
  ];
  const status = STATUS_META[ticket.status];

  return (
    <AppLayout>
      {back}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <h1 className="text-xl font-semibold text-stone-900">
          <span className="font-mono">#{ticket.ticketNumber}</span> · {ticket.categoryDisplayName}
        </h1>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${status?.className ?? ''}`}>{status?.label ?? ticket.status}</span>
      </div>

      {shared ? (
        <p className="mb-4 flex items-start gap-2 rounded-lg bg-violet-50 px-3 py-2 text-sm text-violet-900 ring-1 ring-inset ring-violet-200">
          <Eye className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          The admin team shared this ticket with your Shop so you can follow it. You can read it but not reply.
        </p>
      ) : ticket.status === 'pending' && ticket.estimatedResponseTime ? (
        <p className="mb-4 text-sm text-stone-600">The admin team usually answers within {ticket.estimatedResponseTime}.</p>
      ) : null}

      <ol className="mb-4 grid gap-3" aria-label="Conversation">
        {messages.map((m) => (
          <li key={m.id} className={`grid max-w-[85%] gap-1 ${m.mine ? 'justify-self-end' : 'justify-self-start'}`}>
            <span className={`text-xs text-stone-500 ${m.mine ? 'text-right' : ''}`}>
              <span className="font-semibold text-stone-700">{m.name}</span>
              {m.at ? ` · ${formatInTimezone(m.at, timezone)}` : ''}
            </span>
            <p
              className={`whitespace-pre-wrap break-words rounded-2xl px-4 py-2.5 text-sm ${
                m.mine ? 'bg-emerald-600 text-white' : 'border border-stone-200 bg-white text-stone-800'
              }`}
            >
              {m.text}
            </p>
            <div className={m.mine ? 'justify-self-end' : ''}>
              <ContactAdminImageList images={m.images} />
            </div>
          </li>
        ))}
      </ol>

      {ticket.resolutionNote ? (
        <div className="mb-4 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-900 ring-1 ring-inset ring-emerald-200">
          <span className="font-semibold">Resolution:</span> {ticket.resolutionNote}
        </div>
      ) : null}

      {ticket.canConfirmResolved ? (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-stone-200 bg-white px-4 py-3 text-sm">
          <span className="text-stone-700">Is this solved for your Shop?</span>
          <button
            type="button"
            disabled={busy}
            onClick={() => void confirmSolved()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            <CheckCircle2 className="h-4 w-4" aria-hidden /> Yes, it is solved
          </button>
          <span className="text-stone-500">Not yet? Reply below and the admin team picks it up again.</span>
        </div>
      ) : null}

      {ticket.canReply ? (
        <form onSubmit={send} className="grid gap-2" aria-label="Reply to the admin team">
          <label htmlFor="contact-admin-reply" className="text-sm font-medium text-stone-700">
            Reply
          </label>
          <textarea
            id="contact-admin-reply"
            rows={4}
            maxLength={REPLY_MAX}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Write to the admin team…"
            className="rounded-lg border border-stone-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
          />
          <ContactAdminImagePicker key={pickerKey} onChange={onImages} disabled={busy} />
          <button
            type="submit"
            disabled={busy || uploading || !reply.trim()}
            className="inline-flex items-center gap-2 justify-self-start rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            <Send className="h-4 w-4" aria-hidden /> {busy ? 'Sending…' : 'Send reply'}
          </button>
        </form>
      ) : !shared && ticket.status === 'closed' ? (
        <p className="text-sm text-stone-600">
          This ticket is closed. <Link to={CONTACT_ADMIN_PATH} className="font-medium text-emerald-700 hover:underline">Send a new message</Link> if you need more help.
        </p>
      ) : null}
    </AppLayout>
  );
}
